import svgProgressbarFinished1Src from '@cowprotocol/assets/cow-swap/progressbar-finished-image-1.svg'
import svgProgressbarFinished2Src from '@cowprotocol/assets/cow-swap/progressbar-finished-image-2.svg'
import svgProgressbarFinished3Src from '@cowprotocol/assets/cow-swap/progressbar-finished-image-3.svg'
import svgProgressbarFinished4Src from '@cowprotocol/assets/cow-swap/progressbar-finished-image-4.svg'
import { getAvailableChainsText } from '@cowprotocol/common-const'
import { SupportedChainId } from '@cowprotocol/cow-sdk'

import { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'

/**
 * Frontend-defined step name enum.
 * These are mapped from backend CompetitionOrderStatus.type values
 * but provide more granular UI states for better user experience.
 */
export enum OrderProgressBarStepName {
  INITIAL = 'initial',
  SOLVING = 'solving',
  EXECUTING = 'executing',
  FINISHED = 'finished',
  DELAYED = 'delayed',
  SOLVED = 'solved',
  UNFILLABLE = 'unfillable',
  SUBMISSION_FAILED = 'submissionFailed',
  CANCELLING = 'cancelling',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
  CANCELLATION_FAILED = 'cancellationFailed',
  BRIDGING_IN_PROGRESS = 'bridgingInProgress',
  BRIDGING_FAILED = 'bridgingFailed',
  REFUND_COMPLETED = 'refundCompleted',
  BRIDGING_FINISHED = 'bridgingFinished',
}

export const DEFAULT_STEP_NAME: OrderProgressBarStepName = OrderProgressBarStepName.INITIAL

/**
 * Visual states for progress bar steps UI presentation.
 * These are purely for styling and visual feedback, determining:
 * - Opacity levels (active=1, next=0.6, done=0.3, etc.)
 * - Background colors (active=blue, cancelling=red, etc.)
 * - Animations (spinner for active, lottie for cancelling)
 *
 * Different from STEP_NAMES/OrderProgressBarStepName which represents actual order states
 * mapped from backend CompetitionOrderStatus.type values (e.g., 'initial', 'solving', 'executing').
 */
export enum StepStatus {
  CANCELLING = 'cancelling',
  CANCELLED = 'cancelled',
  EXPIRED = 'expired',
  ACTIVE = 'active',
  NEXT = 'next',
  FUTURE = 'future',
  DONE = 'done',
}
type BridgeStepConfig = (isBridgingTrade: boolean) => StepConfig

type StepConfig = { title: MessageDescriptor; description?: MessageDescriptor }

export const STEPS: (StepConfig | BridgeStepConfig)[] = [
  {
    title: msg`Batching orders`,
  },
  {
    title: msg`The competition has started`,
  },
  { title: msg`Executing`, description: msg`The winner of the competition is now executing your order on-chain.` },
  (isBridgingTrade: boolean) => ({ title: isBridgingTrade ? msg`Start bridging` : msg`Transaction completed` }),
]

export const FINAL_STATES: OrderProgressBarStepName[] = [
  OrderProgressBarStepName.EXPIRED,
  OrderProgressBarStepName.FINISHED,
  OrderProgressBarStepName.CANCELLED,
  OrderProgressBarStepName.CANCELLATION_FAILED,
]

export const COW_SWAP_BENEFITS = [
  msg`CoW Swap solvers search Uniswap, 1inch, Matcha, Sushi and more to find you the best price.`,
  msg`CoW Swap sets the standard for protecting against MEV attacks such as frontrunning and sandwiching.`,
  msg`CoW Swap was the first DEX to offer intent-based trading, gasless swaps, coincidences of wants, and many other DeFi innovations.`,
  msg`CoW Swap is the only exchange that matches Coincidences of Wants (CoWs): peer-to-peer swaps that save on settlement costs.`,
  msg`You can avoid price impact on large trades by using TWAP orders on CoW Swap.`,
  msg`Limit orders on CoW Swap capture surplus - so if the price moves in your favor, you're likely to get more than you asked for.`,
  msg`On CoW Swap, you can set limit orders for balances you don't have yet.`,
  msg`Limit orders on CoW Swap are free to place and cancel. That's unique in DeFi!`,
  msg`You can protect all your Ethereum transactions from MEV - not just trades on CoW Swap - by installing MEV Blocker.`,
  msg`Liquidity pools on CoW AMM grow faster than on other AMMs because they don't lose money to arbitrage bots.`,
  msg`CoW Swap has over 20 active solvers - more than any other exchange.`,
  msg`CoW Swap's robust solver competition protects your slippage from being exploited by MEV bots.`,
  msg`Advanced users can create complex, conditional orders directly through CoW Protocol. Read the docs for more info.`,
  msg`Unlike most other exchanges, CoW Swap doesn't charge you any fees if your trade fails.`,
]

const availableChainsText = getAvailableChainsText()
export const TRADE_ON_NEW_CHAINS_BENEFIT = msg`CoW Swap is live on ${availableChainsText}. Switch the network toggle in the nav bar for quick, cheap transactions.`

export const CHAIN_SPECIFIC_BENEFITS: Record<SupportedChainId, MessageDescriptor[]> = {
  [SupportedChainId.MAINNET]: [TRADE_ON_NEW_CHAINS_BENEFIT, ...COW_SWAP_BENEFITS],
  [SupportedChainId.ARBITRUM_ONE]: COW_SWAP_BENEFITS,
  [SupportedChainId.BASE]: COW_SWAP_BENEFITS,
  [SupportedChainId.GNOSIS_CHAIN]: [TRADE_ON_NEW_CHAINS_BENEFIT, ...COW_SWAP_BENEFITS],
  [SupportedChainId.SEPOLIA]: [TRADE_ON_NEW_CHAINS_BENEFIT, ...COW_SWAP_BENEFITS],
  [SupportedChainId.POLYGON]: COW_SWAP_BENEFITS,
  [SupportedChainId.AVALANCHE]: COW_SWAP_BENEFITS,
  [SupportedChainId.BNB]: COW_SWAP_BENEFITS,
  [SupportedChainId.LINEA]: COW_SWAP_BENEFITS,
  [SupportedChainId.PLASMA]: COW_SWAP_BENEFITS,
  [SupportedChainId.INK]: COW_SWAP_BENEFITS,
  [SupportedChainId.SOLANA]: COW_SWAP_BENEFITS,
}

export const SURPLUS_IMAGES = [
  svgProgressbarFinished1Src,
  svgProgressbarFinished2Src,
  svgProgressbarFinished3Src,
  svgProgressbarFinished4Src,
]
